import AddIcon from '@mui/icons-material/Add'

import {
  AccordionDetails,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetContactByEntityIdQuery } from '../../../app/redux-fetch/apiCoreQuery'
import StyledAccordion from '../../../components/Accordions/StyledAccordion'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import ActionButton from '../../../components/Buttons/ActionButton'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import ToggleContainer from '../../../components/Containers/ToggleContainer'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import commonMessages from '../../../components/Messages/commonMessages'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../components/Typographies/LabelItem'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useLocationModal } from '../hooks/useLocationModal'
import { useOrderStatus } from '../hooks/useOrderStatus'
import FlowSection from './FlowSection'
import TransitDetailsForm from './TransitDetailsForm'
import TransportIcon from './TransportIcon'
import messages from './messages'
import { getAvailableLocations, getLocationAddress, getLocationRevision, mapContactsToOptions } from './utils'

interface WaypointFormProps {
  consignmentIndex: number
  waypointIndex: number
  removeWaypoint: (index: number) => void
  insertWaypoint: (index: number, value: any) => void
  disabled?: boolean;
}

const WaypointForm: React.FC<WaypointFormProps> = ({
  consignmentIndex,
  waypointIndex,
  removeWaypoint,
  insertWaypoint,
  disabled = false,
}) => {
  const { control, setValue, watch } = useFormContext()
  const [expanded, setExpanded] = useState(true)
  const [isOpenTransitDetails, setIsOpenTransitDetails] = useState(true)
  const { formatMessage } = useIntl()
  const { variant } = useOrderFormContext()
  const { isCreated } = useOrderStatus()
  const handleToggleAccordion = () => {
    setExpanded((prev) => !prev)
  }

  const handleToggleTransitDetail = () => {
    setIsOpenTransitDetails((prev) => !prev)
  }

  const customerId = watch('customerId')
  const oldLocations = watch(
    `bookings.${consignmentIndex}.waypoints.${waypointIndex}.locationId`
  )

  const {
    openModal,
    ModalComponent,
    locationOptions,
    locations,
  } = useLocationModal({
    onSave: (id) => {
      setValue(`bookings.${consignmentIndex}.waypoints.${waypointIndex}.locationId`, +id)
    },
    customerId,
    isSelectLocationType: true,
    filterFunction: (location) => {
      return (
        location.enabled &&
        location.locationTypeId !== 0 &&
        location.locationTypeId !== 1
      )
    },
    oldLocations: oldLocations ? [oldLocations] : [],
  })

  const consignments = watch('bookings') || []

  const currentConsignment = consignments[consignmentIndex]

  const usedLocationIds: number[] = []

  if (currentConsignment?.shipFrom?.locationId) {
    usedLocationIds.push(currentConsignment.shipFrom.locationId)
  }

  if (currentConsignment?.deliverTo?.locationId) {
    usedLocationIds.push(currentConsignment.deliverTo.locationId)
  }

  const getLocationName = (locationId: number) => {
    return locationOptions.find((location) => location.value === locationId)?.label
  }

  const locationId = watch(`bookings.${consignmentIndex}.waypoints.${waypointIndex}.locationId`)

  const loadingDate = dayjs(
    watch(`bookings.${consignmentIndex}.waypoints.${waypointIndex}.loadingDate`)
  ).format('YYYY-MM-DD')
  const clearingRequired = watch(
    `bookings.${consignmentIndex}.waypoints.${waypointIndex}.clearingRequired`
  )

  const { data: contacts } = useGetContactByEntityIdQuery(
    locationId,
    {
      skip: !locationId,
      refetchOnMountOrArgChange: true,
    }
  )

  const getContactOptions = useCallback(() => {
    return mapContactsToOptions(contacts)
  }, [contacts])

  const transportMode = watch(
    `bookings.${consignmentIndex}.waypoints.${waypointIndex}.transitDetails.modeOfTransit`
  )

  const selectedLocation = locations.find(
    (l: any) => l.id === locations.find((loc: any) => loc.id === locationId)?.id
  )

  useEffect(() => {
    if (transportMode > -1) return
    if (selectedLocation?.defaultTransportModeId) {
      setValue(
        `bookings.${consignmentIndex}.waypoints.${waypointIndex}.transitDetails.modeOfTransit`,
        selectedLocation?.defaultTransportModeId
      )
    } else {
      setValue(
        `bookings.${consignmentIndex}.waypoints.${waypointIndex}.transitDetails.modeOfTransit`,
        -1
      )
    }
  }, [selectedLocation, locationId, transportMode])

  const contactId = watch(`bookings.${consignmentIndex}.waypoints.${waypointIndex}.contactId`)
  const specialInstructions = watch(`bookings.${consignmentIndex}.waypoints.${waypointIndex}.specialInstructions`)
  const address = getLocationAddress(locations, locationId) || 'N/A'
  const getContactName = (contactId: number) => {
    const contact = (getContactOptions() || []).find((c: any) => c.value === contactId)
    return contact ? contact.label : 'N/A'
  }

  return (
    <>
      {ModalComponent}
      <FlowSection>
        <StyledAccordion expanded={expanded}
          sx={theme => ({
            pt: 0, backgroundColor: theme.palette?.accordion?.parent?.bg,
          })}
          onChange={handleToggleAccordion} >
          <StyledAccordionSummary
            index={`waypoint-content-${consignmentIndex}-${waypointIndex}`}
            expanded={expanded}
          >
            <Grid container>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '-72px' }}>
                <Box display='flex' alignItems='center' width='100%'>
                  <LabelItem>{formatMessage(messages.waypoint)} #{waypointIndex + 1}</LabelItem>
                  {(!disabled && !isCreated) && (
                    <Typography
                      variant='body2'
                      sx={{ color: '#FF6161', cursor: 'pointer', ml: '16px' }}
                      onClick={() => removeWaypoint(waypointIndex)}
                    >
                      {formatMessage(messages.remove)}
                    </Typography>
                  )}
                </Box>
              </Grid>
              {!expanded && (
                <>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '44px' }}>
                    <InfoDisplayItem
                      label={formatMessage(messages.waypoint)}
                      value={getLocationName(locationId) ?? 'N/A'}
                      isSmallScreen={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <InfoDisplayItem
                      label={formatMessage(messages.clearingRequired)}
                      value={clearingRequired ? 'YES' : 'NO'}
                      isSmallScreen={false}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </StyledAccordionSummary>
          <AccordionDetails>
            {variant === 'summary' ? (
              <DefaultSpacingGrid container sx={{ pb: '8px' }}>
                <Grid item xs={12} md={6} sx={{ mr: '-24px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.location)}
                    value={getLocationName(locationId) ?? 'N/A'}
                    isSmallScreen={false}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <InfoDisplayItem
                    label={formatMessage(messages.contact)}
                    value={getContactName(contactId)}
                    isSmallScreen={false}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ mr: '-24px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.address)}
                    value={address}
                    isSmallScreen={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoDisplayItem
                    label={formatMessage(messages.specialInstructions)}
                    value={specialInstructions || 'N/A'}
                    isSmallScreen={false}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoDisplayItem
                    label={formatMessage(messages.clearingRequired)}
                    value={clearingRequired ? 'Yes' : 'No'}
                    isSmallScreen={false}
                  />
                </Grid>
              </DefaultSpacingGrid>
            ) : (
              <DefaultSpacingGrid container>
                <Grid item xs={12} md={6}>
                  <FormSelect
                    name={`bookings.${consignmentIndex}.waypoints.${waypointIndex}.locationId`}
                    label={formatMessage(messages.location)}
                    options={getAvailableLocations(locationOptions, usedLocationIds)}
                    allOptions={locationOptions}
                    onChange={(e) => {
                      setValue(
                        `bookings.${consignmentIndex}.waypoints.${waypointIndex}.locationId`,
                        undefined
                      )
                      const revision = getLocationRevision(locations, +e.target.value)
                      setValue(`bookings.${consignmentIndex}.waypoints.${waypointIndex}.locationRevision`, revision)
                    }}
                    actionLabel={
                      !disabled ? (
                        <ActionButton
                          onClick={() => {
                            openModal()
                          }}
                          label={formatMessage(commonMessages.addNew)}
                        />
                      ) : undefined
                    }
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormSelect
                    name={`bookings.${consignmentIndex}.waypoints.${waypointIndex}.contactId`}
                    label='Contact'
                    options={getContactOptions()}
                    disableLabel={formatMessage(messages.noContactsAvailable)}
                    actionLabel={
                      !disabled ? (
                        <ActionButton
                          onClick={() => {
                            setValue(
                              `bookings.${consignmentIndex}.waypoints.${waypointIndex}.contactId`,
                              null
                            )
                          }}
                          label={formatMessage(messages.clearSelection)}
                        />
                      ) : undefined
                    }
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Typography variant='subtitle1' gutterBottom sx={{ color: '#636363' }}>
                      {formatMessage(messages.address)}
                    </Typography>
                  </Box>
                  <Typography variant='body2' sx={{ color: '#636363' }}>
                    {getLocationAddress(locations, locationId) || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInput
                    name={`bookings.${consignmentIndex}.waypoints.${waypointIndex}.specialInstructions`}
                    label={formatMessage(messages.specialInstructions)}
                    placeholder={formatMessage(commonMessages.pleaseEnter)}
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      height: '100%',
                      pb: '16px',
                    }}
                  >
                    <Controller
                      name={`bookings.${consignmentIndex}.waypoints.${waypointIndex}.clearingRequired`}
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={Boolean(field.value)} disabled={disabled} />}
                          label={formatMessage(messages.clearingRequired)}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </DefaultSpacingGrid>
            )}
          </AccordionDetails>
        </StyledAccordion>
      </FlowSection>
      <FlowSection icon={<TransportIcon id={transportMode} />} showIcon={isOpenTransitDetails}>
        <Box sx={{ p: `${!isOpenTransitDetails ? 0 : variant === 'summary' ? '8px 0 0 24px' : (disabled || isCreated) ? '10px 0 16px 24px' : '10px 0 0 24px'}` }}>
          <ToggleContainer
            title={formatMessage(messages.transitDetails)}
            isOpen={isOpenTransitDetails}
            handleToggle={handleToggleTransitDetail}
            isGrey={true}
            sx={variant === 'summary' ? { pb: '8px', mb: '16px' } : {}}
          >
            <TransitDetailsForm
              namePrefix={`bookings.${consignmentIndex}.waypoints.${waypointIndex}.transitDetails`}
              disabled={disabled}
              consignmentIndex={consignmentIndex}
            />
          </ToggleContainer>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        </Box>
        {!isOpenTransitDetails && (
          <Box display='flex' justifyContent='flex-end'>
            <Button
              onClick={handleToggleTransitDetail}
              sx={{ textTransform: 'none' }}
            >
              {formatMessage(messages.showTransitDetails)}
            </Button>
          </Box>
        )}
        {(!disabled && !isCreated) && (
          <Box display='flex' justifyContent='flex-end'>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                insertWaypoint(waypointIndex + 1, {
                  test: 1,
                  transitDetails: {
                    carrierOption: 'noPreference',
                  },
                })
              }}
              sx={{ textTransform: 'none' }}
            >
              {formatMessage(messages.addWaypoint)}
            </Button>
          </Box>
        )}
      </FlowSection>
    </>
  )
}

export default WaypointForm
